import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AppProvider, useApp } from "@/context/AppContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import Login from "@/pages/Login";
import CompanySelect from "@/pages/CompanySelect";
import MobileApp from "@/pages/MobileApp";
import Dashboard from "@/pages/Dashboard";
import Onboarding from "@/pages/Onboarding";
import Dispatch from "@/pages/Dispatch";
import Compliance from "@/pages/Compliance";
import Receive from "@/pages/Receive";
import Accounting from "@/pages/Accounting";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";

function Boot({ label }) {
  return (
    <div className="min-h-screen grid-bg flex flex-col items-center justify-center gap-3 text-muted-foreground">
      <Loader2 className="h-7 w-7 animate-spin text-primary" />
      <p className="text-sm">{label || "Loading workspace…"}</p>
    </div>
  );
}

function ModuleRoute({ id, children }) {
  const { modules } = useAuth();
  if (!modules.includes(id)) return <Navigate to="/" replace />;
  return children;
}

function Gate() {
  const { ready, company } = useApp();
  const { isAdmin } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (company && loc.pathname === "/" && typeof window !== "undefined" && window.innerWidth < 768) {
      navigate("/m", { replace: true });
    }
  }, [company, loc.pathname, navigate]);
  if (!ready) return <Boot />;
  if (!company) return isAdmin ? <CompanySelect /> : <Boot label="No workspace assigned. Please contact your administrator." />;
  if (loc.pathname.startsWith("/m")) return <MobileApp />;
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/onboarding" element={<ModuleRoute id="onboarding"><Onboarding /></ModuleRoute>} />
        <Route path="/dispatch" element={<ModuleRoute id="dispatch"><Dispatch /></ModuleRoute>} />
        <Route path="/receive" element={<ModuleRoute id="receive"><Receive /></ModuleRoute>} />
        <Route path="/compliance" element={<ModuleRoute id="compliance"><Compliance /></ModuleRoute>} />
        <Route path="/accounting" element={<ModuleRoute id="accounting"><Accounting /></ModuleRoute>} />
        <Route path="/reports" element={<ModuleRoute id="reports"><Reports /></ModuleRoute>} />
        <Route path="/settings" element={<ModuleRoute id="settings"><Settings /></ModuleRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function Shell() {
  const { ready, user } = useAuth();
  if (!ready) return <Boot />;
  if (!user) return <Login />;
  return (
    <AppProvider>
      <Gate />
    </AppProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
